// src/shared/changelog/components/WhatsNewMedia.tsx
import React from "react";
import { ReactComponent as IconConsole } from "@assets/Icons/Icon.svg";
import { ReactComponent as IconServer } from "@assets/Icons/IconServer.svg";
import { ReactComponent as IconKontrol } from "@assets/Icons/IconConnectivityKontrol.svg";
import { ReactComponent as IconReflector } from "@assets/Icons/IconReflector.svg";
import { ReactComponent as IconChart } from "@assets/Icons/IconChart.svg";
import StatusBadge from "@ui-components/Tag/StatusBadge";
import { CHANGELOG_PRODUCT_META, ChangelogProduct } from "../products";
import { ChangelogEntry, CHANGE_CATEGORY_LABEL } from "../types";
import { CATEGORY_SURFACE, CATEGORY_VARIANT } from "./categoryVariant";

/**
 * Product → the mark the SideMenu already uses for it.
 *
 * These are not new artwork: each one is the glyph that product wears in the
 * rail, so the tile reads as "this changed over there" to someone who has the
 * menu in their peripheral vision all day. Reusing the menu's assets is also
 * what keeps the two from drifting — a re-skinned product mark lands here for
 * free.
 *
 * `platform` takes the console's own Home mark, because a platform-wide change
 * belongs to the dashboard rather than to any one product, and `platform-api`
 * takes the server mark for the same reason: it is the surface behind the
 * console, not a screen in it.
 */
const PRODUCT_MARK: Record<
  ChangelogProduct,
  React.FunctionComponent<React.SVGProps<SVGSVGElement>>
> = {
  platform: IconConsole,
  "platform-api": IconServer,
  "kontrol-edge": IconKontrol,
  "reflector-v2": IconReflector,
  "reflector-v1": IconReflector,
  insight: IconChart,
};

interface WhatsNewMediaProps {
  entry: ChangelogEntry;
}

/**
 * The modal's media tile — derived from the entry, never authored (CTX-224).
 *
 * The design put a screenshot here. An entry has no media field and no way to
 * author one, and adding that field is a cross-repo contract change; but the
 * deeper reason not to is that entries get written at cutover, in a hurry, and
 * a slot demanding an asset per entry is exactly the ritual that makes someone
 * reach for `Changelog: none` instead. Art computed from the entry can never be
 * missing, never goes stale, needs no bucket or upload flow, and applies to the
 * whole history for free.
 *
 * Both halves are facts the entry already carries: the **product** picks the
 * mark, the **category** picks the tint. Nothing here is a guess about the
 * change.
 *
 * The finest grain available is the product, not the screen — an entry does not
 * record which menu section it touched, and the backend's `component` is the
 * repo that shipped it. A Licensing change therefore wears the Reflector mark.
 * Buying section-level art means putting a field on the entry contract, and if
 * that price is ever paid it should buy a real screenshot rather than a
 * different icon.
 */
const WhatsNewMedia: React.FC<WhatsNewMediaProps> = ({ entry }) => {
  const { products, category } = entry;

  /* Entries are usually single-product; the first is the one the folder named,
     and it leads the ordering in `CHANGELOG_PRODUCTS`. Cross-product entries
     name the rest beside it rather than stacking marks, which at this size
     turns into a row of specks. */
  const [leadProduct] = products;
  const Mark = leadProduct ? PRODUCT_MARK[leadProduct] : null;
  /* The names are every product the entry belongs to, but the tagline is the
     lead's alone — it explains the mark sitting next to it, and concatenating
     six words per product would turn the subtitle into a sentence nobody
     reads. */
  const tagline = leadProduct
    ? CHANGELOG_PRODUCT_META[leadProduct].tagline
    : null;

  return (
    <div
      className={`flex w-full items-center gap-3 rounded-md border p-4 ${CATEGORY_SURFACE[category]}`}
    >
      {/* The mark sits on the modal's own surface rather than on the category
          tint, which is what makes the pair read as the product's lockup
          instead of as a status icon repeating the badge across from it. */}
      {Mark && (
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-semantic-bg-primary">
          <Mark className="h-6 w-6" aria-hidden />
        </span>
      )}
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-semibold">
          {products.map((p) => CHANGELOG_PRODUCT_META[p].label).join(" · ")}
        </span>
        {tagline && <span className="truncate text-xs opacity-80">{tagline}</span>}
      </span>
      {/* The category rides inside the card (CTX-532): it describes the change
          the lockup stands for, so separating the two left the badge floating
          between the artwork and the title, belonging to neither. */}
      <span className="ml-auto shrink-0">
        <StatusBadge
          variant={CATEGORY_VARIANT[category]}
          withIcon={false}
          label={CHANGE_CATEGORY_LABEL[category]}
        />
      </span>
    </div>
  );
};

export default WhatsNewMedia;
